import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { User } from '@supabase/supabase-js';

interface ChatSession {
  id: string;
  title: string;
  message_count: number;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

interface ChatMessage {
  id: string;
  session_id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  model?: string;
  message_order: number;
  metadata?: any;
  attached_media_url?: string;
  attached_media_type?: string;
  agent_data?: any;
  search_results?: any;
  is_image?: boolean;
  created_at: string;
}

interface LocalMessage {
  id: number | string;
  content: string;
  type: 'user' | 'ai';
  timestamp: Date;
  model?: string;
  selectedAgent?: string;
  attachedMedia?: any;
  isImage?: boolean;
  agentData?: any;
  searchResults?: any;
  reasoning?: string;
  reasoning_content?: string;
  branchedFrom?: { chatId: string | null; messageId: number; summary?: string };
  clientId?: string;
  attachedMediaList?: { url: string; type: string }[];
}

export function useChatStorage(user: User | null) {
  const { toast } = useToast();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const getPersistentAttachedUrl = (msg: any): string | undefined => {
    try {
      const type = msg?.attachedMedia?.type;
      // Do not persist doc-converted preview images to attached_media_url.
      if (type === 'document') return undefined;

      const direct = msg?.attachedMedia?.url;
      if (!direct) return undefined;
      if (typeof direct === 'string' && direct.startsWith('blob:')) return undefined;
      return direct;
    } catch {
      return undefined;
    }
  };

  // Do not persist sensitive/ephemeral agent outputs
  const isSensitiveAgent = (agent: any): boolean => {
    try {
      if (!agent) return false;
      if (typeof agent === 'string') {
        const a = agent.toLowerCase();
        return a === 'password' || a === 'qr-code' || a === 'qr' || a.includes('password') || a.includes('qr');
      }
      const id = (agent.id || agent.slug || agent.key || '').toString().toLowerCase();
      const name = (agent.name || agent.title || '').toString().toLowerCase();
      return (
        id === 'password' || id === 'qr-code' || id === 'qr' ||
        name === 'password' || name === 'qr-code' || name === 'qr' ||
        name.includes('password') || name.includes('qr')
      );
    } catch { return false; }
  };

  // Utility: detect target simple agents
  const isSimpleAgent = (agent: any): boolean => {
    try {
      const s = (typeof agent === 'string' ? agent : (agent?.name || agent?.id || '')).toString().toLowerCase();
      return s.includes('calculator') || s.includes('unit') || s.includes('convert') || s.includes('translate');
    } catch { return false; }
  };

  // Remove any residual HTML for simple agents to ensure only responses are saved
  const stripHtmlIfNeeded = (msg: LocalMessage): LocalMessage => {
    try {
      if (msg?.type !== 'ai') return msg;
      const agentRef = (msg as any).selectedAgent;
      if (!isSimpleAgent(agentRef)) return msg;
      if (typeof msg.content === 'string' && /<[^>]+>/.test(msg.content)) {
        const text = msg.content
          .replace(/<script[\s\S]*?<\/script>/gi, ' ')
          .replace(/<style[\s\S]*?<\/style>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        return { ...msg, content: text } as LocalMessage;
      }
      return msg;
    } catch { return msg; }
  };

  // Sanitize metadata before storing to Supabase
  const sanitizeForStorage = (meta: any) => {
    try {
      const clone = JSON.parse(JSON.stringify(meta || {}));

      // Web search metadata: remove heavy/sensitive fields
      if (clone && clone.searchResults && typeof clone.searchResults === 'object') {
        delete clone.searchResults.query;
        delete clone.searchResults.answer;
        if (Array.isArray(clone.searchResults.images)) {
          clone.searchResults.images = clone.searchResults.images.map((img: any) => {
            if (img && typeof img === 'object') {
              const { description, ...rest } = img;
              return rest;
            }
            return img;
          });
        }
        if (Array.isArray(clone.searchResults.results)) {
          clone.searchResults.results = clone.searchResults.results.map((r: any) => {
            if (r && typeof r === 'object') {
              const { title, content, score, favicon, ...rest } = r;
              return rest;
            }
            return r;
          });
        }
      }

      // Deep research (Parallel) sources: drop excerpt and faviconUrl
      if (clone && Array.isArray(clone.parallelSources)) {
        clone.parallelSources = clone.parallelSources.map((s: any) => {
          if (s && typeof s === 'object') {
            const { excerpt, faviconUrl, ...rest } = s;
            return rest;
          }
          return s;
        });
      }

      return clone;
    } catch {
      return meta;
    }
  };

  // Per-session serialization + message_order cache to avoid 409 conflicts
  const sessionLocksRef = useRef<Map<string, Promise<any>>>(new Map());
  const sessionOrderCacheRef = useRef<Map<string, number>>(new Map());

  const runWithSessionLock = async <T,>(sessionId: string, fn: () => Promise<T>): Promise<T> => {
    const locks = sessionLocksRef.current;
    const prev = locks.get(sessionId) || Promise.resolve();
    let resolveNext: (value?: unknown) => void;
    const next = new Promise((res) => (resolveNext = res));
    locks.set(sessionId, prev.then(() => next));
    try {
      const result = await fn();
      return result;
    } finally {
      // Release
      resolveNext!(undefined);
      // Cleanup if chain resolved
      if (locks.get(sessionId) === next) {
        locks.delete(sessionId);
      }
    }
  };

  // Treat both Postgres unique violations and Supabase HTTP 409s as duplicate conflicts
  const isDuplicateConflict = (error: any): boolean => {
    try {
      if (!error) return false;
      const code = (error.code ?? error?.details?.code ?? '').toString();
      if (code === '23505') return true; // unique_violation
      const status = (error.status ?? error.statusCode ?? '').toString();
      if (status === '409') return true; // HTTP conflict surfaced by PostgREST
      const msg = (
        (error.message ?? '') + ' ' + (error.details ?? '') + ' ' + (error.hint ?? '')
      )
        .toString()
        .toLowerCase();
      return (
        msg.includes('duplicate key') ||
        msg.includes('unique constraint') ||
        msg.includes('conflict')
      );
    } catch {
      return false;
    }
  };

  const refreshOrderCacheFromDb = async (sessionId: string) => {
    try {
      const { data: lastMessage } = await supabase
        .from('chat_messages')
        .select('message_order')
        .eq('session_id', sessionId)
        .order('message_order', { ascending: false })
        .limit(1)
        .maybeSingle();
      const last = lastMessage?.message_order || 0;
      sessionOrderCacheRef.current.set(sessionId, last);
    } catch { }
  };

  const getNextOrderCached = async (sessionId: string): Promise<number> => {
    const cache = sessionOrderCacheRef.current;
    if (cache.has(sessionId)) {
      const n = (cache.get(sessionId) || 0) + 1;
      cache.set(sessionId, n);
      return n;
    }
    // Query once if not cached
    const { data: lastMessage } = await supabase
      .from('chat_messages')
      .select('message_order')
      .eq('session_id', sessionId)
      .order('message_order', { ascending: false })
      .limit(1)
      .maybeSingle();
    const next = (lastMessage?.message_order || 0) + 1;
    cache.set(sessionId, next);
    return next;
  };

  // Create a deterministic idempotency key per message
  const makeClientId = (msg: LocalMessage, role?: 'user' | 'assistant') => {
    const r = role || (msg.type === 'user' ? 'user' : 'assistant');
    const parts = [
      r,
      (msg.content || '').toString().slice(0, 2000),
      JSON.stringify(msg.branchedFrom || {}),
      (msg.attachedMedia?.url || ''),
      // Include a coarse timestamp component so identical texts sent at different times are unique
      // This preserves the ability to dedupe true retries (same client send) while allowing repeats
      (msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date().toISOString()),
    ];
    try {
      // Lightweight hash
      let hash = 0;
      const str = parts.join('|');
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
      }
      return `m_${Math.abs(hash)}`;
    } catch {
      return `m_${Date.now()}`;
    }
  };

  // UUID validator to avoid passing non-UUID IDs (e.g., local storage timestamps) to Supabase
  const isValidUUID = (value: string | null | undefined) => {
    if (!value) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  };

  // Load user's chat sessions
  const loadSessions = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error: any) {
      toast({
        title: "Failed to load chat history",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Create new chat session with AI-generated title
  const createSession = async (
    firstUserMessage: string = 'New Chat',
    meta?: {
      agentId?: string | null;
      agentName?: string | null;
      model?: string | null;
      mode?: 'image' | 'chat-agent' | 'chat-model' | 'web-search' | 'deep-research' | 'quiz' | null;
    }
  ): Promise<string | null> => {
    if (!user) return null;

    try {
      // Insert session immediately with a placeholder title
      const placeholderTitle = 'Generating…';
      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({
          user_id: user.id,
          title: placeholderTitle
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      setSessions(prev => [data, ...prev]);
      setCurrentSessionId(data.id);

      // Fire-and-forget: generate title and update session
      (async () => {
        try {
          const { generateChatTitle } = await import('@/services/titleGeneratorService');
          const aiTitle = await generateChatTitle(firstUserMessage, meta);
          const { error: upErr } = await supabase
            .from('chat_sessions')
            .update({ title: aiTitle })
            .eq('id', data.id)
            .eq('user_id', user.id);
          if (!upErr) {
            // Update local state title optimistically
            setSessions(prev => prev.map(s => s.id === data.id ? { ...s, title: aiTitle, updated_at: new Date().toISOString() } : s));
            // Dispatch event to refresh Sidebar chat list
            window.dispatchEvent(new CustomEvent('messageCountChanged'));
          }
        } catch (_) {
          // Ignore async title update errors; keep placeholder
        }
      })();
      return data.id;
    } catch (error: any) {
      toast({
        title: "Failed to create chat session",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
  };

  // Update session details
  const updateSession = async (sessionId: string, updates: Partial<ChatSession>) => {
    if (!user) return;
    if (!isValidUUID(sessionId)) return;

    try {
      const { error } = await supabase
        .from('chat_sessions')
        .update(updates)
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setSessions(prev =>
        prev.map(session =>
          session.id === sessionId
            ? { ...session, ...updates, updated_at: new Date().toISOString() }
            : session
        )
      );
    } catch (error: any) {
      // Error updating session
    }
  };

  // Save message to current session
  const saveMessage = async (message: LocalMessage, sessionId?: string): Promise<boolean> => {
    if (!user) return false;

    // Resolve target session id only if it's a valid UUID; otherwise create a new one
    let targetSessionId = (sessionId && isValidUUID(sessionId)) ? sessionId : (isValidUUID(currentSessionId || '') ? currentSessionId : null);

    // Create session if none exists or invalid id was provided
    if (!targetSessionId) {
      const firstContent = message.content || 'New Chat';
      targetSessionId = await createSession(firstContent);
      if (!targetSessionId) return false;
    }

    try {
      // Skip persisting assistant messages for sensitive/ephemeral agents (Password, QR)
      if (message.type === 'ai' && isSensitiveAgent((message as any).selectedAgent)) {
        return true;
      }
      // Strip any residual HTML for simple agents to save only text
      if (message.type === 'ai') {
        message = stripHtmlIfNeeded(message);
      }
      // Serialize per session to avoid race for message_order and use cached order
      const ok = await runWithSessionLock<boolean>(targetSessionId, async () => {
        // Build deterministic client id to avoid duplicates
        const clientId = message.clientId || makeClientId(message);

        // Check if message with same client_id already exists
        const { data: existing, error: existErr } = await supabase
          .from('chat_messages')
          .select('id')
          .eq('session_id', targetSessionId)
          .contains('metadata', { client_id: clientId })
          .limit(1)
          .maybeSingle();
        if (existErr) {
          // Non-fatal; proceed
        }
        if (existing?.id) {
          return true;
        }

        const buildDbMessage = (message_order: number) => ({
          session_id: targetSessionId as string,
          user_id: user.id,
          content: message.content,
          role: message.type === 'user' ? 'user' : 'assistant',
          model: message.model,
          message_order,
          is_multi_chat: !!(message as any).multiBatchId,
          multi_chat_id: (message as any).multiBatchId || null,
          multi_order: (message as any).multiOrder ?? null,
          model_alias: (message as any).modelAlias || null,
          metadata: sanitizeForStorage({
            selectedAgent: message.selectedAgent,
            agentData: message.agentData,
            searchResults: message.searchResults,
            parallelSources: (message as any).parallelSources,
            laraResults: (message as any).laraResults,
            isLARA: (message as any).isLARA,
            reasoning: (message as any).reasoning,
            reasoning_content: (message as any).reasoning_content,
            branchedFrom: message.branchedFrom,
            multiBatchId: (message as any).multiBatchId || null,
            isMultiChat: !!(message as any).multiBatchId,
            client_id: clientId,
            timestamp: message.timestamp instanceof Date ? message.timestamp.toISOString() : new Date().toISOString()
          }),
          is_image: message.isImage || false,
          attached_media_url: getPersistentAttachedUrl(message),
          attached_media_type: message.attachedMedia?.type
        });

        // Try multiple times if we hit conflict due to concurrent inserts
        for (let attempt = 0; attempt < 8; attempt++) {
          const nextOrder = await getNextOrderCached(targetSessionId!);
          const dbMessage = buildDbMessage(nextOrder);
          const { error } = await supabase
            .from('chat_messages')
            .insert(dbMessage)
            .select()
            .single();
          if (!error) return true;
          if (isDuplicateConflict(error)) {
            await refreshOrderCacheFromDb(targetSessionId!);
            // small jitter
            await new Promise((r) => setTimeout(r, 20 + Math.floor(Math.random() * 60)));
            continue;
          }
          throw error;
        }
        return true;
      });
      return ok;
    } catch (error: any) {
      // Don't show toast for duplicate/409 messages
      if (!isDuplicateConflict(error)) {
        toast({
          title: "Failed to save message",
          description: error.message,
          variant: "destructive"
        });
      }
      return false;
    }
  };

  // Save a user + assistant pair together in one insert
  const saveMessagePair = async (userMessage: LocalMessage, aiMessage: LocalMessage, sessionId?: string): Promise<boolean> => {
    if (!user) return false;

    // Resolve target session id only if it's a valid UUID; otherwise create a new one
    let targetSessionId = (sessionId && isValidUUID(sessionId)) ? sessionId : (isValidUUID(currentSessionId || '') ? currentSessionId : null);

    if (!targetSessionId) {
      const firstContent = userMessage?.content || 'New Chat';
      targetSessionId = await createSession(firstContent);
      if (!targetSessionId) return false;
    }

    try {
      const ok = await runWithSessionLock<boolean>(targetSessionId!, async () => {

        const toDbMessage = (msg: LocalMessage, role: 'user' | 'assistant', message_order: number, client_id: string) => ({
          session_id: targetSessionId as string,
          user_id: user!.id,
          content: msg.content,
          role,
          model: msg.model,
          message_order,
          is_multi_chat: !!(msg as any).multiBatchId,
          multi_chat_id: (msg as any).multiBatchId || null,
          multi_order: (msg as any).multiOrder ?? null,
          model_alias: (msg as any).modelAlias || null,
          metadata: sanitizeForStorage({
            selectedAgent: (msg as any).selectedAgent,
            agentData: (msg as any).agentData,
            searchResults: (msg as any).searchResults,
            parallelSources: (msg as any).parallelSources,
            laraResults: (msg as any).laraResults,
            isLARA: (msg as any).isLARA,
            reasoning: (msg as any).reasoning,
            reasoning_content: (msg as any).reasoning_content,
            branchedFrom: (msg as any).branchedFrom,
            multiBatchId: (msg as any).multiBatchId || null,
            isMultiChat: !!(msg as any).multiBatchId,
            client_id,
            timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : new Date().toISOString()
          }),
          is_image: !!msg.isImage,
          attached_media_url: getPersistentAttachedUrl(msg as any),
          attached_media_type: (msg as any).attachedMedia?.type
        });

        // Normalize AI assistant content for simple agents (strip HTML)
        aiMessage = stripHtmlIfNeeded(aiMessage);

        const userClientId = userMessage.clientId || makeClientId(userMessage, 'user');
        const aiClientId = aiMessage.clientId || makeClientId(aiMessage, 'assistant');

        // Check existence for each by client_id
        const toInsert: Array<{ row: any; role: 'user' | 'assistant' }> = [];
        try {
          const { data: existUser } = await supabase
            .from('chat_messages')
            .select('id')
            .eq('session_id', targetSessionId as string)
            .contains('metadata', { client_id: userClientId })
            .limit(1)
            .maybeSingle();
          if (!existUser?.id) {
            toInsert.push({ row: { base: userMessage, role: 'user', clientId: userClientId }, role: 'user' });
          }
        } catch { }
        // Only persist assistant if not from sensitive agents (Password, QR)
        if (!isSensitiveAgent((aiMessage as any).selectedAgent)) {
          try {
            const { data: existAi } = await supabase
              .from('chat_messages')
              .select('id')
              .eq('session_id', targetSessionId as string)
              .contains('metadata', { client_id: aiClientId })
              .limit(1)
              .maybeSingle();
            if (!existAi?.id) {
              toInsert.push({ row: { base: aiMessage, role: 'assistant', clientId: aiClientId }, role: 'assistant' });
            }
          } catch { }
        }

        if (toInsert.length === 0) {
          return true;
        }

        // Try multiple times reserving orders if we hit conflict
        for (let attempt = 0; attempt < 8; attempt++) {
          const order1 = await getNextOrderCached(targetSessionId!);
          let order2: number | undefined;
          if (toInsert.length === 2) {
            order2 = await getNextOrderCached(targetSessionId!);
          }

          const rows = toInsert.map((entry) => {
            // Ensure user message always gets the lower order number
            const desiredOrder = toInsert.length === 2
              ? (entry.role === 'user' ? order1 : (order2 as number))
              : order1;
            const base = (entry.row as any).base as LocalMessage;
            const cid = (entry.row as any).clientId as string;
            return toDbMessage(base, entry.role, desiredOrder, cid);
          });

          // Sort rows to ensure user message (lower order) comes before AI message (higher order)
          rows.sort((a, b) => a.message_order - b.message_order);

          const { error } = await supabase
            .from('chat_messages')
            .insert(rows)
            .select();

          if (!error) return true;
          if (isDuplicateConflict(error)) {
            await refreshOrderCacheFromDb(targetSessionId!);
            continue;
          }
          throw error;
        }
        return true;
      });
      return ok;
    } catch (error: any) {
      if (!isDuplicateConflict(error)) {
        toast({
          title: 'Failed to save messages',
          description: error.message,
          variant: 'destructive',
        });
      }
      return false;
    }
  };

  // Load messages for a session with optional pagination
  const loadMessages = async (
    sessionId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<LocalMessage[]> => {
    if (!user) return [];

    try {
      let query = supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .eq('user_id', user.id)
        .order('message_order', { ascending: true });

      // Apply pagination if specified
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;

      const result: LocalMessage[] = [];
      for (const dbMessage of (data || [])) {
        const metadata = dbMessage.metadata as any;
        let url: string | undefined = dbMessage.attached_media_url || undefined;

        // If private storage info is present, generate a fresh signed URL
        try {
          const attachedStorage = metadata?.agentData?.attachedStorage || dbMessage.agent_data?.attachedStorage;
          if (attachedStorage?.bucket && attachedStorage?.path) {
            const signed = await supabase.storage.from(attachedStorage.bucket).createSignedUrl(attachedStorage.path, 60 * 60 * 24 * 7);
            if (!signed.error && signed.data?.signedUrl) {
              url = signed.data.signedUrl;
            }
          }
        } catch { }

        const rawSearch = metadata?.searchResults || dbMessage.search_results;
        const hydratedSearch = (() => {
          if (!rawSearch || typeof rawSearch !== 'object') return undefined;
          const hasResults = Array.isArray(rawSearch.results) && rawSearch.results.length > 0;
          const hasImages = Array.isArray(rawSearch.images) && rawSearch.images.length > 0;
          return hasResults || hasImages ? rawSearch : undefined;
        })();

        const metaDoc = (metadata?.agentData?.attachedMedia && metadata.agentData.attachedMedia.type === 'document')
          ? metadata.agentData.attachedMedia
          : undefined;

        const metaImages: string[] | undefined = Array.isArray(metadata?.agentData?.attachedImageUrls)
          ? (metadata.agentData.attachedImageUrls as string[]).filter((u: any) => typeof u === 'string' && u.startsWith('http'))
          : undefined;

        const docPreviewUrls: string[] | undefined = Array.isArray(metaDoc?.convertedImageUrls)
          ? (metaDoc.convertedImageUrls as string[])
          : (Array.isArray(metadata?.agentData?.attachedMedia?.convertedImageUrls)
            ? (metadata.agentData.attachedMedia.convertedImageUrls as string[])
            : undefined);

        const hydratedAttachedMedia = (() => {
          // If a real attachment is persisted (typically an image), prefer that as the primary attachment.
          // Doc previews should live in metadata and be rendered separately.
          if (url && dbMessage.attached_media_type) {
            return { url, type: dbMessage.attached_media_type, name: metadata?.agentData?.attachedMedia?.name };
          }

          // Otherwise, rehydrate document from metadata so refresh shows filename
          if (metaDoc) {
            const first = docPreviewUrls?.[0];
            return {
              type: 'document',
              url: first || '',
              name: metaDoc?.name,
              pageCount: metaDoc?.pageCount,
              originalExt: metaDoc?.originalExt,
            };
          }

          return undefined;
        })();

        result.push({
          id: dbMessage.id,
          content: dbMessage.content,
          type: dbMessage.role === 'user' ? 'user' : 'ai',
          timestamp: new Date(dbMessage.created_at),
          model: dbMessage.model,
          selectedAgent: metadata?.selectedAgent,
          agentData: metadata?.agentData || dbMessage.agent_data,
          searchResults: hydratedSearch,
          parallelSources: metadata?.parallelSources,
          reasoning: metadata?.reasoning,
          reasoning_content: metadata?.reasoning_content,
          isImage: dbMessage.is_image,
          branchedFrom: metadata?.branchedFrom,
          attachedMedia: hydratedAttachedMedia,
          attachedMediaList: Array.isArray(docPreviewUrls)
            ? (docPreviewUrls as string[]).map((u: string) => ({ url: u, type: 'image' }))
            : (Array.isArray(metaImages)
              ? (metaImages as string[]).map((u: string) => ({ url: u, type: 'image' }))
              : undefined),
          // Rehydrate specialized payloads for renderers
          weatherData: metadata?.agentData?.weatherData || dbMessage?.agent_data?.weatherData,
          // Rehydrate LARA consolidated results
          laraResults: metadata?.laraResults,
          isLARA: metadata?.isLARA,
          // Prefer columns for multi-AI grouping, fallback to metadata
          multiBatchId: dbMessage.multi_chat_id || metadata?.multiBatchId || undefined,
          isMultiChat: (dbMessage.is_multi_chat ?? metadata?.isMultiChat) || false,
          multiOrder: dbMessage.multi_order ?? undefined,
          modelAlias: dbMessage.model_alias ?? undefined,
        } as LocalMessage);
      }
      return result;
    } catch (error: any) {
      toast({
        title: "Failed to load messages",
        variant: "destructive"
      });
      return [];
    }
  };

  // Get total message count for a session (for pagination)
  const getMessageCount = async (sessionId: string): Promise<number> => {
    if (!user) return 0;

    try {
      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId)
        .eq('user_id', user.id);

      if (error) throw error;
      return count || 0;
    } catch (error: any) {
      return 0;
    }
  };

  // Delete a session
  const deleteSession = async (sessionId: string) => {
    if (!user) return;
    if (!isValidUUID(sessionId)) return;

    try {
      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', sessionId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Remove from local state
      setSessions(prev => prev.filter(session => session.id !== sessionId));

      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
      }
    } catch (error: any) {
      toast({
        title: "Failed to delete chat",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Load sessions when user changes
  useEffect(() => {
    if (user) {
      loadSessions();
    } else {
      setSessions([]);
      setCurrentSessionId(null);
    }
  }, [user]);

  return {
    sessions,
    currentSessionId,
    setCurrentSessionId,
    loading,
    createSession,
    updateSession,
    saveMessage,
    saveMessagePair,
    loadMessages,
    getMessageCount,
    deleteSession,
    refreshSessions: loadSessions
  };
}